import React from 'react';
import { motion } from 'framer-motion';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
  limit: number;
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  hasNext,
  hasPrev,
  total,
  limit,
  className = ''
}) => {
  const startItem = (currentPage - 1) * limit + 1;
  const endItem = Math.min(currentPage * limit, total);

  const getVisiblePages = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 9; // Show up to 9 page buttons
    
    // If total pages is small, show all
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }
    
    // Always show first 3 pages
    pages.push(1, 2, 3);
    
    // Calculate range around current page
    let startPage = Math.max(4, currentPage - 1);
    let endPage = Math.min(totalPages - 1, currentPage + 2);
    
    // Adjust range if near the start (pages 1-5)
    if (currentPage <= 5) {
      startPage = 4;
      endPage = Math.min(totalPages - 1, 6);
    } else if (currentPage >= totalPages - 2) {
      // Near the end
      startPage = Math.max(4, totalPages - 4);
    }
    
    // Add dots before middle pages if needed
    if (startPage > 4) {
      pages.push('...');
    }
    
    // Add middle pages (skip if already added in first 3)
    for (let i = startPage; i <= endPage; i++) {
      if (i > 3 && i < totalPages) {
        pages.push(i);
      }
    }
    
    // Add dots after middle pages if needed
    if (endPage < totalPages - 1) {
      pages.push('...');
    }
    
    // Always show last page
    if (totalPages > 3) {
      pages.push(totalPages);
    }
    
    return pages;
  };

  return (
    <div className={`flex items-center justify-between ${className}`}>
      {/* Results info */}
      <div className="text-sm text-gray-600 dark:text-gray-400 font-['SF_Pro_Text',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif]">
        Showing {startItem} to {endItem} of {total} conversations
      </div>

      {/* Pagination controls */}
      <div className="flex items-center gap-2">
        {/* Previous button */}
        <motion.button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!hasPrev}
          className={`
            flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200
            ${hasPrev 
              ? 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500' 
              : 'text-gray-400 dark:text-gray-600 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 cursor-not-allowed'
            }
          `}
          whileHover={hasPrev ? { scale: 1.02 } : {}}
          whileTap={hasPrev ? { scale: 0.98 } : {}}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Previous
        </motion.button>

        {/* Page numbers */}
        <div className="flex items-center gap-1">
          {getVisiblePages().map((page, index) => (
            page === '...' ? (
              <span key={`dots-${index}`} className="px-3 py-2 text-gray-400 dark:text-gray-500">
                ...
              </span>
            ) : (
              <motion.button
                key={page}
                onClick={() => onPageChange(page as number)}
                className={`
                  px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200
                  ${page === currentPage
                    ? 'bg-gray-900 dark:bg-gray-700 text-white shadow-sm'
                    : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }
                `}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {page}
              </motion.button>
            )
          ))}
        </div>

        {/* Next button */}
        <motion.button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!hasNext}
          className={`
            flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200
            ${hasNext 
              ? 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500' 
              : 'text-gray-400 dark:text-gray-600 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 cursor-not-allowed'
            }
          `}
          whileHover={hasNext ? { scale: 1.02 } : {}}
          whileTap={hasNext ? { scale: 0.98 } : {}}
        >
          Next
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </motion.button>
      </div>
    </div>
  );
};

export default Pagination;